import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Container,
  Card, // Import Card from Material-UI
  CardContent, // Import CardContent from Material-UI
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  FormHelperText,
  Input,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios"; // Import Axios for making HTTP requests
import NotificationMessage from "./NotificationMessage";

function CottonHybridForm() {
  const [hybrid, setHybrid] = useState("");
  const [hybridTag, setHybridTag] = useState("");
  const [treatmentPackage, setTreatmentPackage] = useState("");
  const [hybridTagImage, setHybridTagImage] = useState(null);
  const [cost, setCost] = useState("");
  const [notes, setNotes] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const teamName = localStorage.getItem("username");
  const [submittedForms, setSubmittedForms] = useState([]);
  const token = localStorage.getItem("token");
  const isFormDisabled = submittedForms.length > 0;

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const hybridOptions = [
    { name: "NG3195B3XF", cost: 645, treatment: "CottlyST Base Plus" },
    { name: "NG4190B3XF", cost: 645, treatment: "CottlyST Base Plus" },
    { name: "PHY411W3FE", cost: 720, treatment: "Trio Treated" },
    { name: "PHY415W3FE", cost: 720, treatment: "Trio Treated" },
    { name: "DP2038B3XF", cost: 700, treatment: "Acceleron Standard" },
    { name: "DP2414B3TXF", cost: 815, treatment: "Acceleron Elite" },
    { name: "DP2522NRB3TXF", cost: 815, treatment: "Acceleron Elite" },
    { name: "DP2537B3TXF", cost: 815, treatment: "Acceleron Elite" },
    { name: "DP2127B3XF", cost: 700, treatment: "Acceleron Standard" },
    { name: "DP2333B3XF", cost: 780, treatment: "Acceleron Elite" },
    { name: "DP2328B3TXF", cost: 825, treatment: "Acceleron Elite" },
    { name: "Other", cost: null, treatment: null },
  ];

  const hybridCosts = {};

  const handleHybridClick = (selectedHybridName) => {
    const found = hybridOptions.find((h) => h.name === selectedHybridName);

    if (found && found.cost) {
      const formattedHybrid = `${found.name} / MSRP: $${found.cost}\n(${found.treatment})`;
      setHybrid(formattedHybrid);
      setCost(found.cost.toString());
      setHybridTag("");
      setTreatmentPackage("");
      setHybridTagImage(null);
    } else {
      setHybrid(selectedHybridName);
      setCost("");
      setHybridTag("");
      setTreatmentPackage("");
      setHybridTagImage(null);
    }

    setErrorMsg("");
    setSuccessMsg("");
  };

  const fetchSubmittedForms = async () => {
    try {
      const username = localStorage.getItem("username");
      const response = await axios.post(
        "/api/getCottonHybridSubmittedForms",
        { username }, // Send username as part of the request body
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the Authorization header
          },
        },
      );

      if (response.status === 200) {
        // Update the state with the fetched data
        setSubmittedForms(response.data);

        if (response.data && response.data.length > 0) {
          const submittedHybrid = response.data[0].hybrid;
          const matchingOption = hybridOptions.find((option) =>
            submittedHybrid.includes(option.name),
          );
          if (matchingOption && matchingOption.cost) {
            const formattedHybrid = `${matchingOption.name} / MSRP: $${matchingOption.cost}\n(${matchingOption.treatment})`;
            setHybrid(formattedHybrid);
          } else {
            setHybrid(submittedHybrid);
          }
        }
      } else {
        console.error("Failed to fetch submitted forms data");
      }
    } catch (error) {
      console.error("Error fetching submitted forms data:", error);
    }
  };

  useEffect(() => {
    // Fetch the submitted forms data for the logged-in user
    // Call the fetch function when the component mounts
    fetchSubmittedForms();
  }, []);

  const handleHybridTagImageChange = (event) => {
    setHybridTagImage(event.target.files[0]);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!hybrid) {
      setErrorMsg("Please fill in all inputs");
      return;
    }

    if (hybrid === "Other" && !hybridTag.trim()) {
      setErrorMsg("Please enter the hybrid tag name");
      return;
    }

    if (hybrid === "Other" && !treatmentPackage.trim()) {
      setErrorMsg("Please enter the treatment package");
      return;
    }

    if (hybrid === "Other" && !cost) {
      setErrorMsg("Please fill in all inputs");
      return;
    }

    if (hybrid === "Other" && !hybridTagImage) {
      setErrorMsg("Please upload a hybrid tag image");
      return;
    }

    if (hybrid === "Other" && hybridTagImage) {
      try {
        const imageFormData = new FormData();
        imageFormData.append("file", hybridTagImage);
        imageFormData.append("teamName", teamName);
        imageFormData.append("cropType", "cotton");

        await axios.post("/api/uploadCottonHybridTagImage", imageFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error("Error uploading hybrid tag image:", error);
        setStatusMsg("Error uploading hybrid tag image");
        showNotification("Error uploading hybrid tag image", "error");
        return;
      }
    }

    const hybridName =
      hybrid === "Other"
        ? treatmentPackage
          ? `${hybridTag} ${treatmentPackage}`
          : hybridTag
        : hybrid;

    // Prepare the data to be sent to the backend
    const formData = {
      hybrid: hybridName,
      cost: parseFloat(cost),
      notes,
      teamName,
    };

    try {
      // Make an HTTP POST request to your backend API using Axios
      const response = await axios.post(
        "/api/cottonhybridsubmit", // Replace with your actual backend URL
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the Authorization header
          },
        },
      );

      if (response.status === 200) {
        // Form submission was successful
        fetchSubmittedForms();
        setStatusMsg("Form submitted successfully");
        setErrorMsg("");
        setSuccessMsg("Input submitted successfully!");
        showNotification("Hybrid form submitted successfully!", "success");

        setHybrid("");
        setHybridTag("");
        setTreatmentPackage("");
        setHybridTagImage(null);
        setCost("");
        setNotes("");
      } else {
        // Form submission failed
        setStatusMsg("Form submission failed");
        showNotification("Form submission failed", "error");
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      setStatusMsg("Form submission failed");
      showNotification("Form submission failed", "error");
    }
  };

  console.log("Submitted Forms:", submittedForms);

  return (
    <Container>
      <Grid item xs={12}>
        <Typography variant="h5">Hybrids to use:</Typography>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {hybridOptions.map((option) => (
            <Card
              key={option.name}
              onClick={() => !isFormDisabled && handleHybridClick(option.name)}
              sx={{
                cursor: isFormDisabled ? "not-allowed" : "pointer",
                pointerEvents: isFormDisabled ? "none" : "auto",
                opacity: isFormDisabled ? 0.6 : 1,
                margin: "4px",
                padding: "10px 16px",
                backgroundColor:
                  hybrid.includes(option.name) || hybrid === option.name
                    ? "#fa4616"
                    : "#F5F5F5",
                border:
                  hybrid.includes(option.name) || hybrid === option.name
                    ? "2px solid rgb(255, 255, 255)"
                    : "2px solid rgb(37, 106, 185)",
                borderRadius: "12px",
                color:
                  hybrid.includes(option.name) || hybrid === option.name
                    ? "white"
                    : "#333",
                boxShadow:
                  hybrid.includes(option.name) || hybrid === option.name
                    ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                    : "none",
                transition: "all 0.3s ease-in-out",
                display: "flex",
                justifyContent: "center", // Center horizontally
                alignItems: "center", // Center vertically
                textAlign: "center", // Ensures text stays centered
                height: "50px", // Fixed height for better alignment
                "&:hover": {
                  backgroundColor:
                    hybrid.includes(option.name) || hybrid === option.name
                      ? "#d73a12"
                      : "#E0E0E0",
                  transform: "scale(1.05)",
                },
              }}
            >
              <CardContent>
                <Typography variant="body2">
                  {option.name} {option.cost ? `/ MSRP: $${option.cost}` : ""}
                  {option.treatment && (
                    <>
                      <br />({option.treatment})
                    </>
                  )}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      </Grid>
      <Typography variant="h4" gutterBottom>
        My Form
      </Typography>
      {hybrid === "Other" && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 2, fontStyle: "italic" }}
        >
          <strong>How to fill in your hybrid details:</strong> Enter the
          complete hybrid tag from your seed bag, the treatment package
          (required), the MSRP price per bag, and upload a hybrid tag image.
        </Typography>
      )}
      <form onSubmit={handleFormSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">
              <label
                style={{
                  display: "inline-block",
                  marginRight: "16px",
                  alignContent: "center",
                }}
              >
                Hybrid
              </label>
            </Typography>
          </Grid>
          {hybrid === "Other" ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="hybrid-tag-input"
                  label="Hybrid Tag"
                  variant="outlined"
                  fullWidth
                  value={hybridTag}
                  onChange={(e) => setHybridTag(e.target.value)}
                  placeholder="Enter your hybrid tag"
                  required
                  disabled={isFormDisabled}
                  helperText="Enter the complete hybrid tag/name from your seed bag"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="treatment-package-input"
                  label={
                    <>
                      Treatment Package <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  variant="outlined"
                  fullWidth
                  value={treatmentPackage}
                  onChange={(e) => setTreatmentPackage(e.target.value)}
                  placeholder="e.g., ThryvOn, etc."
                  disabled={isFormDisabled}
                  helperText="Seed treatment package - Required"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="cost-input"
                  label="MSRP"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="Enter price per bag"
                  required
                  disabled={isFormDisabled}
                  helperText="$/bag"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <Input
                    id="hybrid-tag-image-input"
                    type="file"
                    onChange={handleHybridTagImageChange}
                    inputProps={{ accept: "image/*" }}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="hybrid-tag-image-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={isFormDisabled}
                      sx={{
                        padding: "12px",
                        borderStyle: "dashed",
                        borderWidth: "2px",
                        textTransform: "none",
                      }}
                    >
                      {hybridTagImage ? (
                        hybridTagImage.name
                      ) : (
                        <>
                          Upload Hybrid Tag Image{" "}
                          <span style={{ color: "red" }}>*</span>
                        </>
                      )}
                    </Button>
                  </label>
                  <FormHelperText>
                    Upload a clear picture of your hybrid tag from the seed bag
                    (jpg, jpeg, png format) - Required
                  </FormHelperText>
                </FormControl>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <TextField
                  id="hybrid-input"
                  label="Hybrid"
                  variant="outlined"
                  fullWidth
                  multiline
                  value={hybrid}
                  onChange={(e) => setHybrid(e.target.value)}
                  placeholder="Hybrid"
                  required
                  disabled={isFormDisabled}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField
              id="notes-input"
              label="Notes"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes"
              disabled={isFormDisabled}
            />
          </Grid>
          {!isFormDisabled && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  color: "#d32f2f",
                  fontWeight: 500,
                  fontStyle: "italic",
                  mb: 1,
                }}
              >
                ⚠️ Once submitted, selection cannot be changed
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              id="hybrid-selection-submit"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isFormDisabled || hybrid === ""}
            >
              {isFormDisabled ? "Form Already Submitted" : "SUBMIT"}
            </Button>
            {errorMsg && (
              <Typography variant="body2" sx={{ color: "red", mt: 1 }}>
                {errorMsg}
              </Typography>
            )}
            {successMsg && (
              <Typography variant="body2" sx={{ color: "green", mt: 1 }}>
                {successMsg}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Typography variant="subtitle1" id="hybrid-status-msg">
              {statusMsg}
            </Typography>
          </Grid>
        </Grid>
      </form>
      {/* {submittedForms.length > 0 && (
        <div>
          <Typography variant="h5" gutterBottom>
            Submitted Data
          </Typography>
          {submittedForms.map((form, index) => (
            <Card key={index} sx={{ marginBottom: 2 }}>
              <CardContent>
                <Typography variant="h6">Hybrid: {form.hybrid}</Typography>
                <Typography variant="body1">
                  Cost: ${form.cost} per bag
                </Typography>
                <Typography variant="body2">Notes: {form.notes}</Typography>
                <Typography variant="body2">
                  Team Name: {form.teamName}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Container>
  ); */}
      {submittedForms.length > 0 && (
        <div>
          <strong>
            {" "}
            <Typography variant="h5" gutterBottom>
              Submitted Data
              <br></br>
            </Typography>{" "}
          </strong>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Hybrid</b>
                </TableCell>
                <TableCell>
                  <b>Cost per Bag</b>
                </TableCell>
                <TableCell>
                  <b>Notes</b>
                </TableCell>
                {/* <TableCell>
                    <b>Team Name</b>
                  </TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {submittedForms.map((form, index) => {
                const isPredefined = hybridOptions.some(
                  (h) => form.hybrid.includes(h.name) && h.cost,
                );
                return (
                  <TableRow key={index}>
                    <TableCell style={{ whiteSpace: "pre-line" }}>
                      {form.hybrid}
                    </TableCell>
                    <TableCell>
                      {isPredefined ? `Default $${form.cost}` : `$${form.cost}`}
                    </TableCell>
                    <TableCell>{form.notes}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <NotificationMessage
        open={notification.open}
        onClose={handleCloseNotification}
        message={notification.message}
        severity={notification.severity}
      />
    </Container>
  );
}

export default CottonHybridForm;
